/**
 * The amendment tables' two questions a screen has to answer (finding A2,
 * 2026-09-28): which earlier months it may be built against, and what one
 * line should say about the result.
 *
 * Both kept out of `Periods.tsx` so a test can hold them — the first because
 * offering a month with no filed return held would send a person into a
 * `NOT_FOUND`, the second because the result has claims that must not blur:
 * a filed document missing from our books is LISTED and never zeroed, and a
 * table whose base table this app does not book (EXPA, ATA, TXPA today) is
 * NOT BUILT rather than empty.
 */

import type { AmendmentTable, Amendments, AmendmentsBody, PeriodRecord } from './api'

/** Each amendment table as the return names it (B-05 added six to W3-02's three). */
export const TABLE_LABELS: Record<AmendmentTable, string> = {
  b2ba: 'B2BA — amended B2B invoices (9A)',
  b2cla: 'B2CLA — amended B2C (Large) invoices (9A)',
  expa: 'EXPA — amended exports (9A)',
  b2csa: 'B2CSA — amended B2C (Others) (10)',
  cdnra: 'CDNRA — amended credit/debit notes, registered (9C)',
  cdnura: 'CDNURA — amended credit/debit notes, unregistered (9C)',
  ata: 'ATA — amended advances received (11A)',
  txpa: 'TXPA — amended advances adjusted (11B)',
  ecoa: '14A — amended supplies through an e-commerce operator',
}

function tableName(name: string): string {
  return (TABLE_LABELS[name as AmendmentTable] ?? name.toUpperCase()).split(' — ')[0] ?? name
}

function monthIndex(period: string): number {
  const [mm, yyyy] = period.split('-')
  return Number(yyyy) * 12 + Number(mm)
}

/**
 * Earlier GSTR-1 months of this client whose filed return the service holds —
 * a snapshot (imported FROM the filed return) or a filing confirmed by
 * uploading it. A month merely MARKED filed on an ARN holds no return, so it
 * is not offered: naming it would only earn a refusal. The same month is not
 * offered either — V-10 sends a same-period correction to GSTR-1A.
 */
export function originalCandidates(rows: PeriodRecord[], current: PeriodRecord): string[] {
  return rows
    .filter(
      (r) =>
        r.return_type === 'GSTR1' &&
        r.client_id === current.client_id &&
        (r.origin === 'snapshot' || !!r.filed_confirmed_at) &&
        monthIndex(r.period) < monthIndex(current.period),
    )
    .map((r) => r.period)
    .filter((p, i, all) => all.indexOf(p) === i)
    .sort((a, b) => monthIndex(b) - monthIndex(a))
}

function count(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`
}

/** One line for the top of the result. */
export function amendmentHeadline(result: Amendments): string {
  if (!result.applicable) return result.message ?? 'These amendments cannot be built.'
  const tables = Object.entries(result.tables)
    .filter(([, rows]) => (rows ?? []).length > 0)
    .map(([name, rows]) => `${count(rows!.length, 'row', 'rows')} in ${tableName(name)}`)
  const parts = [
    tables.length > 0
      ? `Against ${result.original_period}: ${tables.join(', ')}.`
      : `Against ${result.original_period}: no document has changed since it was filed.`,
  ]
  if (result.unchanged) parts.push(`${count(result.unchanged, 'document is', 'documents are')} unchanged.`)
  const missing = result.filed_not_in_books?.length ?? 0
  if (missing > 0)
    parts.push(
      `${count(missing, 'filed document is', 'filed documents are')} not in the books — listed below, ` +
        'not zeroed: removing a filed invoice is a decision, not an amendment.',
    )
  return parts.join(' ')
}

/** One sentence per table that is named and not built: an empty table would claim
 *  there is nothing to amend, which is a different statement. */
export function unbuiltSentences(result: Amendments): string[] {
  return Object.entries(result.unbuilt ?? {})
    .filter(([, reason]) => !!reason)
    .map(([name, reason]) => `${tableName(name)} is not built: ${reason}`)
}

/** What happens to a build now (B-06): it is kept on this period, and this period's
 *  utility workbook and GSTN JSON write it — or, refused, nothing is kept. */
export function keptSentence(body: AmendmentsBody): string | null {
  const result = body.amendments
  if (!result.applicable || !body.amendments_artifact_id) return null
  const rows = Object.values(result.tables).reduce((n, rs) => n + (rs?.length ?? 0), 0)
  if (rows === 0)
    return (
      'Kept on this period. With nothing to amend, the utility workbook and the GSTN JSON ' +
      'carry no amendment sheet or section.'
    )
  return (
    `Kept on this period: its utility workbook and GSTN JSON write these rows in the ` +
    'amendment sheets and sections, and leave the amended documents out of the ordinary ' +
    'tables so each is filed once. If the books change after this, the GSTN JSON holds, and ' +
    'the utility workbook names the amendments it left out, until they are built again. ' +
    'Building against another month replaces this build.'
  )
}
